import { DocumentNode } from "@apollo/client"
import { AccountInfo } from "@azure/msal-browser"
import { HAUFE_OPERATIONS, haufeLoadingManager } from "contexts/HaufeLoadingContext"
import { action, Action, createStore, thunk, Thunk } from "easy-peasy"
import { GET_ARTICLES } from "graphql/queries/articles"
import { enqueueSnackbar } from "notistack"

import { formatTime } from "utils/dateTimeUtils"
import { v4 } from "uuid"
import { CREATE_CONTACT, UPDATE_CONTACT } from "../graphql/mutations/contacts"
import { CREATE_CUSTOMER, UPDATE_CUSTOMER } from "../graphql/mutations/customers"
import { CREATE_SALES_QUOTE, UPDATE_SALES_QUOTE } from "../graphql/mutations/salesQuotes"
import {
  DELETE_CRITERIA_CATALOG,
  DELETE_DEVICE,
  DELETE_DEVICE_DATA_GROUP,
  DELETE_FILE,
  DELETE_LABORATORY,
  DELETE_SKILL,
  DELETE_TASK_CONFIG,
  DELETE_TEXT_BLOCK,
  GET_ALL_REPORTS_BASIC_DATA,
  GET_CRITERIA_CATALOGS,
  GET_DEVICE_DATA_GROUPS,
  GET_DEVICES_BY_TYPE,
  GET_EXAMINATION_CONFIGS,
  GET_LABORATORIES,
  GET_NORMS,
  GET_PROJECTS,
  GET_SKILLS,
  GET_TASKS_CONFIG,
  GET_TEXT_BLOCKS,
  SET_EXAM_CONFIGS_OBSOLETE,
  SET_NORMS_DELETED,
  UPDATE_TASK_WORKING_TIME,
  UPLOAD_FILE,
  UPSERT_CRITERIA_CATALOG,
  UPSERT_DEVICE_AUX,
  UPSERT_DEVICE_DATA_GROUP,
  UPSERT_DEVICE_EUT_GROUP,
  UPSERT_DEVICE_TEST_EQUIPMENT,
  UPSERT_EXAMINATION,
  UPSERT_EXAMINATION_CONFIG,
  UPSERT_LABORATORY,
  UPSERT_NORM,
  UPSERT_PROJECT,
  UPSERT_REPORT,
  UPSERT_SKILL,
  UPSERT_TASK_CONFIG,
  UPSERT_TEXT_BLOCK,
} from "../graphql/queries"
import { GET_CONTACT_BY_ID, GET_CONTACTS } from "../graphql/queries/contacts"
import { GET_CUSTOMER_BY_ID, GET_CUSTOMERS } from "../graphql/queries/customers"
import { GET_EMPLOYEES } from "../graphql/queries/employees"
import { GET_LEADS, GET_LEADS_ARCHIVE } from "../graphql/queries/leads"
import {
  GET_SALES_QUOTE_BY_QUOTE_NBR_WITH_DETAILS,
  GET_SALES_QUOTE_DETAILS,
  GET_SALES_QUOTES,
  GET_SALES_QUOTES_BY_OPPORTUNITY_IDS,
} from "../graphql/queries/salesQuotes"
import { GET_USERS } from "../graphql/queries/users"
import {
  Article,
  Chapter,
  ChapterInput,
  Contact,
  CreateContactInput,
  CreateCustomerInput,
  CreateSalesQuoteFromOpportunityInput,
  CriteriaCatalog,
  Customer,
  DataNode,
  DataNodeInput,
  Device,
  DeviceAux,
  DeviceDataGroup,
  DeviceEutGroup,
  DeviceTestEquipment,
  DeviceType,
  Employee,
  Examination,
  ExaminationConfig,
  ExaminationConfigInput,
  FileUploadInput,
  GetArticlesQuery,
  Laboratory,
  Lead,
  Norm,
  NormenImportInput,
  Person,
  Project,
  Report,
  SalesQuote,
  SalesQuoteDetail,
  SalesQuoteWithDetails,
  Skill,
  TaskConfig,
  TextBlock,
  UpdateContactInput,
  UpdateCustomerInput,
  UpdateSalesQuoteInput,
  User,
} from "../graphql/types"
import { apolloClient } from "./apolloClient"
import { dummyVisitors } from "./dummyData/dummyData"
import { IMPORT_NORMEN } from "./mutations"

type InputReportUpdate = {
  projectID: string
  report: Report
}

export type NormTagFilterState = {
  searchTerm: string
  selectedTagIds: string[]
  showOnlyRelevant: boolean
}

export const DEFAULT_TAG_FILTER: NormTagFilterState = {
  searchTerm: "",
  selectedTagIds: [],
  showOnlyRelevant: true,
}

export const emptyPerson: User = {
  id: "",
  displayName: "",
  email: "",
  userPrincipalName: "",
}

export interface StoreModel {
  users: User[]
  projects: Project[]
  visitors: Person[]
  testEquipment: Device[]
  auxEquipment: Device[]
  activeTaskID: string | null
  eutGroups: DeviceEutGroup[]
  leads: Lead[]
  leadsArchive: Lead[]
  contacts: Contact[]
  customers: Customer[]
  salesQuotes: SalesQuote[]
  salesQuoteDetails: SalesQuoteDetail[]
  reports: Report[]
  criteriaCatalogs: CriteriaCatalog[]

  normFilters: {
    admin: NormTagFilterState
    project: Record<string, NormTagFilterState>
  }

  authenticatedUser: AccountInfo | null

  textBlocks: TextBlock[]
  skills: Skill[]
  examinations: Examination[]
  norms: Norm[]
  deviceDataGroups: DeviceDataGroup[]
  taskConfigs: TaskConfig[]
  examinationConfigs: ExaminationConfig[]
  laboratories: Laboratory[]
  articles: Article[]
  employees: Employee[]
  times: Record<string, number>
  totalTime: string
  activeCard: string | null

  fetchLeads: Thunk<StoreModel, { fresh?: boolean }>
  fetchLeadsArchive: Thunk<StoreModel, { fresh?: boolean }>
  fetchContacts: Thunk<StoreModel, { fresh?: boolean }>
  fetchCustomers: Thunk<StoreModel, { fresh?: boolean }>
  fetchSalesQuotes: Thunk<StoreModel, { fresh?: boolean }>
  fetchArticles: Thunk<StoreModel, { fresh?: boolean }>
  fetchUsers: Thunk<StoreModel, { fresh?: boolean } | undefined>
  fetchVisitors: Action<StoreModel>
  fetchTestEquipment: Thunk<StoreModel, { fresh?: boolean } | undefined>
  fetchAuxEquipment: Thunk<StoreModel, { fresh?: boolean } | undefined>
  fetchTextBlocks: Thunk<StoreModel, { fresh?: boolean } | undefined>
  fetchNorms: Thunk<StoreModel, { fresh?: boolean } | undefined>
  fetchDeviceDataGroups: Thunk<StoreModel, { fresh?: boolean } | undefined>
  fetchTaskConfigs: Thunk<StoreModel, { fresh?: boolean } | undefined>
  fetchExaminationConfigs: Thunk<StoreModel, { fresh?: boolean } | undefined>
  fetchLaboratories: Thunk<StoreModel, { fresh?: boolean } | undefined>
  fetchSkills: Thunk<StoreModel, { fresh?: boolean } | undefined>
  fetchProjects: Thunk<StoreModel, { fresh?: boolean; retries?: number }>
  fetchEmployees: Thunk<StoreModel, { fresh?: boolean } | undefined>
  fetchDevicesByType: Thunk<StoreModel, { type: DeviceType; fresh?: boolean } | undefined>
  fetchAllReportsBasicData: Thunk<StoreModel, { fresh?: boolean } | undefined>
  fetchCriteriaCatalogs: Thunk<StoreModel, { fresh?: boolean } | undefined>

  startTimer: Action<StoreModel, string>
  stopTimer: Action<StoreModel, string>
  incrementTime: Action<StoreModel>
  calculateTotalTime: Action<StoreModel>

  updateLead: Action<StoreModel, Lead>
  updateReport: Thunk<StoreModel, InputReportUpdate>
  updateProject: Thunk<StoreModel, Project & { noSnackbar?: boolean }>
  updateEutGroup: Thunk<StoreModel, { deviceEutGroup: DeviceEutGroup; isOffer: boolean }>
  updateAux: Thunk<StoreModel, DeviceAux>
  updateSkill: Thunk<StoreModel, Skill>
  updateTextBlock: Thunk<StoreModel, TextBlock>
  updateNorm: Thunk<StoreModel, Norm>
  updateDeviceDataGroup: Thunk<StoreModel, DeviceDataGroup>
  updateTaskConfig: Thunk<StoreModel, TaskConfig>
  updateTestEquipment: Thunk<StoreModel, DeviceTestEquipment>
  updateAuxEquipment: Thunk<StoreModel, DeviceAux>
  updateExaminationConfig: Thunk<StoreModel, ExaminationConfig>
  updateLaboratory: Thunk<StoreModel, Laboratory>
  updateExamination: Thunk<StoreModel, Examination>
  updateTaskWorkingTime: Thunk<
    StoreModel,
    { taskId: string; examinationId: string; workingTime: number }
  >
  updateCriteriaCatalog: Thunk<StoreModel, CriteriaCatalog>

  uploadFile: Thunk<StoreModel, FileUploadInput>

  deleteSkill: Thunk<StoreModel, string[]>
  deleteTextBlock: Thunk<StoreModel, string[]>
  deleteNorm: Thunk<StoreModel, string[]>
  setNormsDeleted: Thunk<StoreModel, { ids: string[]; deleted: boolean; deletedAt?: string }>
  deleteDeviceDataGroup: Thunk<StoreModel, string[]>
  deleteTaskConfig: Thunk<StoreModel, string[]>
  deleteTestEquipment: Thunk<StoreModel, string[]>
  deleteAuxEquipment: Thunk<StoreModel, string[]>
  deleteExaminationConfig: Thunk<StoreModel, string[]>
  setExaminationConfigsObsolete: Thunk<
    StoreModel,
    { ids: string[]; obsolete: boolean; obsoleteAt?: string }
  >
  deleteLaboratory: Thunk<StoreModel, string[]>
  deleteFile: Thunk<StoreModel, string[]>
  deleteCriteriaCatalog: Thunk<StoreModel, string[]>

  setCriteriaCatalogs: Action<StoreModel, CriteriaCatalog[]>
  setTextBlocks: Action<StoreModel, TextBlock[]>
  setNorms: Action<StoreModel, Norm[]>
  setDeviceDataGroups: Action<StoreModel, DeviceDataGroup[]>
  setTaskConfigs: Action<StoreModel, TaskConfig[]>
  setExaminationConfigs: Action<StoreModel, ExaminationConfig[]>
  setLaboratories: Action<StoreModel, Laboratory[]>
  setSkills: Action<StoreModel, Skill[]>
  setTestEquipment: Action<StoreModel, Device[]>
  setAuxEquipment: Action<StoreModel, Device[]>
  setContacts: Action<StoreModel, Contact[]>
  setCustomers: Action<StoreModel, Customer[]>
  setSalesQuotes: Action<StoreModel, SalesQuote[]>
  setArticles: Action<StoreModel, Article[]>
  setUsers: Action<StoreModel, User[]>
  setSalesQuoteDetails: Action<StoreModel, SalesQuoteDetail[]>
  setProjects: Action<StoreModel, Project[]>
  setEutGroups: Action<StoreModel, DeviceEutGroup[]>
  setAuthenticatedUser: Action<StoreModel, AccountInfo | null>
  setEmployees: Action<StoreModel, Employee[]>
  setReports: Action<StoreModel, Report[]>
  setNormTagFilter: Action<
    StoreModel,
    | { scope: "admin"; value: Partial<NormTagFilterState> }
    | { scope: "project"; projectId: string; value: Partial<NormTagFilterState> }
  >
  resetNormTagFilter: Action<
    StoreModel,
    { scope: "admin" } | { scope: "project"; projectId: string }
  >

  createSalesQuote: Thunk<StoreModel, CreateSalesQuoteFromOpportunityInput>
  updateSalesQuote: Thunk<StoreModel, UpdateSalesQuoteInput>
  getSalesQuoteDetails: Thunk<StoreModel, { quoteID: string }>
  getSalesQuoteByQuoteNbrWithDetails: Thunk<
    StoreModel,
    { quoteNbr: string },
    Promise<SalesQuoteWithDetails | null>
  >
  createContact: Thunk<StoreModel, CreateContactInput, Promise<Contact | null>>
  createCustomer: Thunk<StoreModel, CreateCustomerInput, Promise<Customer | null>>
  updateCustomer: Thunk<StoreModel, UpdateCustomerInput, Promise<Customer | null>>
  getCustomerById: Thunk<
    StoreModel,
    { customerID: string; fresh?: boolean },
    Promise<Customer | null>
  >
  updateContact: Thunk<StoreModel, UpdateContactInput, Promise<Contact | null>>
  getContactById: Thunk<StoreModel, { contactID: string; fresh?: boolean }, Promise<Contact | null>>

  getSalesQuotesByOpportunityIDs: Thunk<
    StoreModel,
    { opportunityIDs: string[]; fresh?: boolean },
    Promise<SalesQuote[][] | null>
  >
  importNormenFromExcel: Thunk<
    StoreModel,
    NormenImportInput,
    Promise<{ success: boolean; message: string }>
  >
}

// Unified helper to execute GraphQL operations through Apollo while returning
// a normalized response with status + errors while preserving the legacy shape
// (i.e., response.data.data.<field>) for backwards compatibility.
const graphqlRequest = async (
  query: DocumentNode,
  variables?: Record<string, any>
): Promise<any> => {
  const isMutation =
    query.definitions[0]?.kind === "OperationDefinition" &&
    (query.definitions[0] as any).operation === "mutation"

  const result = isMutation
    ? await apolloClient.mutate({ mutation: query, variables })
    : await apolloClient.query({
        query,
        variables,
        // Use cache-first by default; allow explicit bypass via variables.fresh
        fetchPolicy: variables && variables.fresh ? "no-cache" : "cache-first",
      })

  const errors = (result as any).errors as any[] | undefined
  const data = (result as any).data ?? null
  const status: "ok" | "partial" | "error" =
    errors && errors.length > 0 ? (data ? "partial" : "error") : "ok"

  // Preserve legacy Axios-like shape while adding normalized top-level fields
  const legacyPayload: any = { data }
  if (errors) legacyPayload.errors = errors

  return { data: legacyPayload, errors, status }
}

// Test-only export to allow unit tests to validate normalization behavior
export { graphqlRequest as __graphqlRequestForTest }

// Simple in-flight request de-duplication to avoid duplicate network calls
const inflight: Record<string, Promise<any>> = {}
const withInflight = <T>(key: string, fn: () => Promise<T>): Promise<T> => {
  const existing = inflight[key]
  if (existing) return existing
  const p = fn().finally(() => {
    delete inflight[key]
  })
  inflight[key] = p
  return p
}

export const store = createStore<StoreModel>({
  users: [],
  projects: [],
  visitors: [],
  testEquipment: [],
  auxEquipment: [],
  eutGroups: [],
  textBlocks: [],
  skills: [],
  activeTaskID: null,
  examinations: [],
  norms: [],
  deviceDataGroups: [],
  taskConfigs: [],
  examinationConfigs: [],
  laboratories: [],
  articles: [],
  leads: [],
  leadsArchive: [],
  contacts: [],
  customers: [],
  salesQuotes: [],
  criteriaCatalogs: [],
  authenticatedUser: null,
  salesQuoteDetails: [],
  employees: [],
  reports: [],

  normFilters: {
    admin: { ...DEFAULT_TAG_FILTER },
    project: {},
  },

  times: {},
  totalTime: "",
  activeCard: null,

  fetchLeads: thunk(async (actions, payload = { fresh: false }, { getState }) => {
    const state = getState()
    if (state.leads.length > 0 && !payload.fresh) {
      return
    }

    // Signal start of Haufe operation
    haufeLoadingManager.startHaufeOperation(HAUFE_OPERATIONS.FETCH_LEADS)

    try {
      console.log(`Fetching opportunities from API${payload.fresh ? " (fresh data)" : ""}...`)

      const response = await graphqlRequest(GET_LEADS, {
        fresh: payload.fresh,
      })

      const state = getState()

      if (response.data?.data?.getLeads && Array.isArray(response.data.data.getLeads)) {
        const leads = response.data.data.getLeads
        console.log("Found opportunities data:", leads.length)
        state.leads = leads || []
      } else {
        console.error("Could not find opportunity data in the GraphQL response")
        console.log("Response data:", response.data)
        state.leads = []
      }
    } catch (error) {
      console.error("Error fetching opportunities:", error)
      const state = getState()
      state.leads = []
    } finally {
      // Signal end of Haufe operation
      haufeLoadingManager.endHaufeOperation()
    }
  }),

  fetchLeadsArchive: thunk(async (actions, payload = { fresh: false }, { getState }) => {
    const state = getState()
    if (state.leadsArchive.length > 0 && !payload.fresh) {
      console.log("Leads archive already in store, not refetching.")
      return
    }
    try {
      console.log(`Fetching opportunities from API${payload.fresh ? " (fresh data)" : ""}...`)

      const response = await graphqlRequest(GET_LEADS_ARCHIVE, {
        fresh: payload.fresh,
      })

      const state = getState()

      if (
        response.data?.data?.getLeadsArchive &&
        Array.isArray(response.data.data.getLeadsArchive)
      ) {
        const leadsArchive = response.data.data.getLeadsArchive
        // console.log("Found opportunities data:", leads)
        state.leadsArchive = leadsArchive || [] // Ensure we always set an array
      } else {
        console.error("Could not find opportunity data in the GraphQL response")
        console.log("Response data:", response.data)
        state.leadsArchive = [] // Set empty array on error
      }
    } catch (error) {
      console.error("Error fetching opportunities:", error)
      const state = getState()
      state.leadsArchive = [] // Set empty array on error
    }
  }),

  fetchContacts: thunk(async (actions, payload = { fresh: false }, { getState }) => {
    const state = getState()
    if (state.contacts.length > 0 && !payload.fresh) {
      console.log("Contacts already in store, not refetching.")
      return
    }

    // Signal start of Haufe operation
    haufeLoadingManager.startHaufeOperation(HAUFE_OPERATIONS.FETCH_CONTACTS)

    try {
      console.log(
        `Fetching contacts from API via GraphQL${payload.fresh ? " (fresh data)" : ""}...`
      )
      const response = await graphqlRequest(GET_CONTACTS, {
        fresh: payload.fresh,
      })
      if (response.data?.data?.getContacts) {
        actions.setContacts(response.data.data.getContacts)
        console.log("Contacts fetched via GraphQL:", response.data.data.getContacts.length)
      } else {
        console.error("Could not find contact data in GraphQL response:", response.data)
        actions.setContacts([])
      }
    } catch (error) {
      console.error("Error fetching contacts via GraphQL:", error)
      actions.setContacts([])
      throw error
    } finally {
      // Signal end of Haufe operation
      haufeLoadingManager.endHaufeOperation()
    }
  }),

  fetchCustomers: thunk(async (actions, payload = { fresh: false }, { getState }) => {
    const state = getState()
    if (state.customers.length > 0 && !payload.fresh) {
      console.log("Customers already in store, not refetching.")
      return
    }

    // Signal start of Haufe operation
    haufeLoadingManager.startHaufeOperation(HAUFE_OPERATIONS.FETCH_CUSTOMERS)

    try {
      console.log(
        `Fetching customers from API via GraphQL${payload.fresh ? " (fresh data)" : ""}...`
      )
      const response = await graphqlRequest(GET_CUSTOMERS, {
        fresh: payload.fresh,
      })
      if (response.data?.data?.getCustomers) {
        actions.setCustomers(response.data.data.getCustomers)
        console.log("Customers fetched via GraphQL:", response.data.data.getCustomers.length)
      } else {
        console.error("Could not find customer data in GraphQL response:", response.data)
        actions.setCustomers([])
      }
    } catch (error) {
      console.error("Error fetching customers via GraphQL:", error)
      actions.setCustomers([])
      throw error
    } finally {
      // Signal end of Haufe operation
      haufeLoadingManager.endHaufeOperation()
    }
  }),

  fetchSalesQuotes: thunk(async (actions, payload = { fresh: false }, { getState }) => {
    const state = getState()
    if (state.salesQuotes.length > 0 && !payload.fresh) {
      console.log("Sales quotes already in store, not refetching.")
      return
    }

    // Signal start of Haufe operation
    haufeLoadingManager.startHaufeOperation(HAUFE_OPERATIONS.FETCH_SALES_QUOTES)

    try {
      console.log(
        `Fetching sales quotes from API via GraphQL${payload.fresh ? " (fresh data)" : ""}...`
      )
      const response = await graphqlRequest(GET_SALES_QUOTES, {
        fresh: payload.fresh,
      })
      if (response.data?.data?.getSalesQuotes) {
        actions.setSalesQuotes(response.data.data.getSalesQuotes)
        console.log("Sales quotes fetched via GraphQL:", response.data.data.getSalesQuotes.length)
      } else {
        console.error("Could not find sales quotes data in GraphQL response:", response.data)
        actions.setSalesQuotes([])
      }
    } catch (error) {
      console.error("Error fetching sales quotes via GraphQL:", error)
      actions.setSalesQuotes([])
      throw error
    } finally {
      // Signal end of Haufe operation
      haufeLoadingManager.endHaufeOperation()
    }
  }),

  fetchArticles: thunk(async (actions, payload = { fresh: false }, { getState }) => {
    const state = getState()
    if (state.articles.length > 0 && !payload.fresh) {
      console.log("Articles already in store, not refetching.")
      return
    }
    try {
      console.log(
        `Fetching articles from API via GraphQL${payload.fresh ? " (fresh data)" : ""}...`
      )

      const response = await graphqlRequest(GET_ARTICLES, {
        fresh: payload.fresh,
      })

      // Use the generated type for type safety
      const data = response.data?.data as GetArticlesQuery | undefined

      if (data?.getArticles) {
        actions.setArticles(data.getArticles)
      } else {
        actions.setArticles([])
      }
    } catch (error) {
      console.error("Error fetching articles via GraphQL:", error)
      actions.setArticles([])
      throw error
    }
  }),

  fetchUsers: thunk(async (actions, payload = { fresh: false }, { getState }) => {
    const state = getState()
    if (state.users.length > 0 && !payload.fresh) {
      console.log("Users already in store, not refetching.")
      return
    }
    try {
      console.log("Fetching all users from API via GraphQL...")
      const response = await graphqlRequest(GET_USERS)
      if (response.data?.data?.getUsers) {
        actions.setUsers(response.data.data.getUsers)
        console.log("All users fetched via GraphQL:", response.data.data.getUsers.length)
      } else {
        console.error("Could not find users data in GraphQL response:", response.data)
        actions.setUsers([])
      }
    } catch (error) {
      console.error("Error fetching all users via GraphQL:", error)
      actions.setUsers([])
      throw error
    }
  }),

  fetchVisitors: action((state) => {
    state.visitors = dummyVisitors
  }),

  fetchSkills: thunk(async (actions, payload = { fresh: false }, { getState }) => {
    const state = getState()
    if (state.skills.length > 0 && !payload.fresh) {
      console.log("Skills already in store, not refetching.")
      return
    }
    try {
      const { data } = await apolloClient.query({
        query: GET_SKILLS,
        fetchPolicy: "no-cache",
      })
      actions.setSkills(data.getSkills)
    } catch (error) {
      console.error("Error fetching skills:", error)
      actions.setSkills([])
      throw error
    }
  }),

  fetchCriteriaCatalogs: thunk(async (actions, payload = { fresh: false }, { getState }) => {
    const state = getState()
    if (state.criteriaCatalogs.length > 0 && !payload.fresh) {
      console.log("Criteria catalogs already in store, not refetching.")
      return
    }
    try {
      const { data } = await apolloClient.query({
        query: GET_CRITERIA_CATALOGS,
        fetchPolicy: "no-cache",
      })
      actions.setCriteriaCatalogs(data.getCriteriaCatalogs)
    } catch (error) {
      console.error("Error fetching criteria catalogs:", error)
      actions.setCriteriaCatalogs([])
      throw error
    }
  }),

  fetchTestEquipment: thunk(async (actions, payload = { fresh: false }, { getState }) => {
    const state = getState()
    if (state.testEquipment.length > 0 && !payload.fresh) {
      console.log("Test equipment already in store, not refetching.")
      return
    }
    try {
      const { data } = await apolloClient.query({
        query: GET_DEVICES_BY_TYPE,
        fetchPolicy: "no-cache",
        variables: {
          type: "TEST_EQUIPMENT",
        },
      })
      actions.setTestEquipment(data.getDevicesByType)
    } catch (error) {
      console.error("Error fetching test equipment:", error)
      actions.setTestEquipment([])
      throw error
    }
  }),

  fetchAuxEquipment: thunk(async (actions, payload = { fresh: false }, { getState }) => {
    const state = getState()
    if (state.auxEquipment.length > 0 && !payload.fresh) {
      console.log("AUX equipment already in store, not refetching.")
      return
    }
    try {
      const { data } = await apolloClient.query({
        query: GET_DEVICES_BY_TYPE,
        fetchPolicy: "no-cache",
        variables: {
          type: "AUX",
        },
      })
      actions.setAuxEquipment(data.getDevicesByType)
    } catch (error) {
      console.error("Error fetching AUX equipment:", error)
      actions.setAuxEquipment([])
      throw error
    }
  }),

  fetchTextBlocks: thunk(async (actions, payload = { fresh: false }, { getState }) => {
    const state = getState()
    if (state.textBlocks.length > 0 && !payload.fresh) {
      console.log("Text blocks already in store, not refetching.")
      return
    }
    try {
      const { data } = await apolloClient.query({
        query: GET_TEXT_BLOCKS,
        fetchPolicy: "no-cache",
      })
      actions.setTextBlocks(data.getTextBlocks || [])
    } catch (error) {
      console.error("Error fetching text blocks:", error)
      actions.setTextBlocks([])
      throw error
    }
  }),

  fetchNorms: thunk(async (actions, payload = { fresh: false }, { getState }) => {
    const state = getState()
    if (state.norms.length > 0 && !payload.fresh) {
      console.log("Norms already in store, not refetching.")
      return
    }
    try {
      const { data } = await apolloClient.query({
        query: GET_NORMS,
        fetchPolicy: "no-cache",
        variables: { includeDeleted: true },
      })
      actions.setNorms(data.getNorms || [])
    } catch (error) {
      console.error("Error fetching norms:", error)
      actions.setNorms([])
      throw error
    }
  }),

  fetchDeviceDataGroups: thunk(async (actions, payload = { fresh: false }, { getState }) => {
    const state = getState()
    if (state.deviceDataGroups.length > 0 && !payload.fresh) {
      console.log("Device data groups already in store, not refetching.")
      return
    }
    try {
      const { data } = await apolloClient.query({
        query: GET_DEVICE_DATA_GROUPS,
        fetchPolicy: "no-cache",
      })
      actions.setDeviceDataGroups(data.getDeviceDataGroups || [])
    } catch (error) {
      console.error("Error fetching device data groups:", error)
      actions.setDeviceDataGroups([])
      throw error
    }
  }),

  fetchTaskConfigs: thunk(async (actions, payload = { fresh: false }, { getState }) => {
    const state = getState()
    if (state.taskConfigs.length > 0 && !payload.fresh) {
      console.log("Task configs already in store, not refetching.")
      return
    }
    try {
      const { data } = await apolloClient.query({
        query: GET_TASKS_CONFIG,
        fetchPolicy: "no-cache",
      })
      actions.setTaskConfigs(data.getTaskConfigs || [])
    } catch (error) {
      console.error("Error fetching task configs:", error)
      actions.setTaskConfigs([])
      throw error
    }
  }),

  fetchExaminationConfigs: thunk(async (actions, payload = { fresh: false }, { getState }) => {
    const state = getState()
    if (state.examinationConfigs.length > 0 && !payload.fresh) {
      console.log("Examination configs already in store, not refetching.")
      return
    }
    try {
      const { data } = await withInflight(
        "exam-configs:" + (payload.fresh ? "fresh" : "cached"),
        () =>
          apolloClient.query({
            query: GET_EXAMINATION_CONFIGS,
            fetchPolicy: payload.fresh ? "no-cache" : "cache-first",
          })
      )
      actions.setExaminationConfigs(data.getExaminationConfigs)
    } catch (error) {
      console.error("Error fetching examination configs:", error)
      actions.setExaminationConfigs([])
      throw error
    }
  }),

  fetchLaboratories: thunk(async (actions, payload = { fresh: false }, { getState }) => {
    const state = getState()
    if (state.laboratories.length > 0 && !payload.fresh) {
      console.log("Laboratories already in store, not refetching.")
      return
    }
    try {
      const { data } = await withInflight("labs:" + (payload.fresh ? "fresh" : "cached"), () =>
        apolloClient.query({
          query: GET_LABORATORIES,
          fetchPolicy: payload.fresh ? "no-cache" : "cache-first",
        })
      )
      actions.setLaboratories(data.getLaboratories)
    } catch (error) {
      console.error("Error fetching laboratories:", error)
      actions.setLaboratories([])
      throw error
    }
  }),

  fetchProjects: thunk(async (actions, payload = { fresh: false, retries: 3 }, { getState }) => {
    const state = getState()
    if (state.projects.length > 0 && !payload.fresh) {
      console.log("Projects already in store, not refetching.")
      return
    }
    let attempts = payload.retries || 3
    while (attempts > 0) {
      try {
        const { data } = await withInflight(
          "projects:" + (payload.fresh ? "fresh" : "cached"),
          () =>
            apolloClient.query({
              query: GET_PROJECTS,
              fetchPolicy: payload.fresh ? "no-cache" : "cache-first",
            })
        )
        actions.setProjects(data.getProjects || [])
        return // Success
      } catch (error) {
        console.error(`Error fetching projects (attempt ${4 - attempts}):`, error)
        attempts--
        if (attempts > 0) {
          await new Promise((resolve) => setTimeout(resolve, 1000)) // 1s delay
        }
      }
    }
    enqueueSnackbar("Fehler beim Laden der Projekte nach mehreren Versuchen", {
      variant: "error",
    })
    throw new Error("Failed to fetch projects")
  }),

  fetchEmployees: thunk(async (actions, payload = { fresh: false }, { getState }) => {
    const state = getState()
    if (state.employees.length > 0 && !payload.fresh) {
      console.log("Employees already in store, not refetching.")
      return
    }
    try {
      const { data } = await withInflight("employees:" + (payload.fresh ? "fresh" : "cached"), () =>
        apolloClient.query({
          query: GET_EMPLOYEES,
          fetchPolicy: payload.fresh ? "no-cache" : "cache-first",
        })
      )
      if (data.getEmployees) {
        const activeEmployees = data.getEmployees.filter(
          (employee: any) => employee.Status === "Active"
        )
        actions.setEmployees(activeEmployees)
      } else {
        actions.setEmployees([])
      }
    } catch (error) {
      console.error("Error fetching employees:", error)
      actions.setEmployees([])
    }
  }),

  fetchDevicesByType: thunk(
    async (actions, payload = { type: DeviceType.EUT_GROUP, fresh: false }, { getState }) => {
      const state = getState()
      if (state.eutGroups.length > 0 && !payload.fresh) {
        console.log("EUTGroups already in store, not refetching.")
        return
      }
      try {
        const { data } = await withInflight(
          `devices:${payload.type}:` + (payload.fresh ? "fresh" : "cached"),
          () =>
            apolloClient.query({
              query: GET_DEVICES_BY_TYPE,
              fetchPolicy: payload.fresh ? "no-cache" : "cache-first",
              variables: { type: payload.type },
            })
        )
        if (data.getDevicesByType) {
          actions.setEutGroups(data.getDevicesByType)
        } else {
          actions.setEutGroups([])
        }
      } catch (error) {
        console.error("Error fetching EUTGroups:", error)
        actions.setEutGroups([])
        throw error
      }
    }
  ),

  getSalesQuotesByOpportunityIDs: thunk(
    async (actions, payload: { opportunityIDs: string[]; fresh: false }, { getState }) => {
      try {
        const response = await graphqlRequest(GET_SALES_QUOTES_BY_OPPORTUNITY_IDS, {
          opportunityIDs: payload.opportunityIDs,
          fresh: payload.fresh,
        })

        if (response.data?.data?.getSalesQuotesByOpportunityIDs) {
          const grouped = response.data.data.getSalesQuotesByOpportunityIDs as SalesQuote[][]
          const flat: SalesQuote[] = grouped.flat()

          const state = getState()
          const existing = [...state.salesQuotes]

          flat.forEach((q) => {
            const idx = existing.findIndex((e) => e.id === q.id)
            if (idx !== -1) existing[idx] = q
            else existing.push(q)
          })

          actions.setSalesQuotes(existing)
          return grouped
        } else {
          console.error(
            "Could not find sales quotes in getSalesQuotesByOpportunityIDs GraphQL response or error occurred:",
            response.data?.errors
          )
          return null
        }
      } catch (error) {
        console.error(`Error fetching sales quotes for multiple opportunities:`, error)
        return null
      }
    }
  ),

  fetchAllReportsBasicData: thunk(async (actions, payload = { fresh: false }, { getState }) => {
    const state = getState()
    if (state.reports.length > 0 && !payload.fresh) {
      console.log("Reports already in store, not refetching.")
      return
    }
    try {
      const { data } = await withInflight("reports:" + (payload.fresh ? "fresh" : "cached"), () =>
        apolloClient.query({
          query: GET_ALL_REPORTS_BASIC_DATA,
          fetchPolicy: payload.fresh ? "no-cache" : "cache-first",
        })
      )
      actions.setReports(data.getAllReportsBasicData || [])
    } catch (error) {
      console.error("Error fetching all reports basic data:", error)
      actions.setReports([])
      throw error
    }
  }),

  updateLead: action((state, payload) => {
    state.leads = state.leads.map((item) => {
      if (item.id === payload.id) {
        return payload
      } else {
        return item
      }
    })
  }),

  startTimer: action((state, cardTitle) => {
    state.activeCard = cardTitle
  }),

  stopTimer: action((state, cardTitle) => {
    if (state.activeCard === cardTitle) {
      state.activeCard = null
    }
  }),

  incrementTime: action((state) => {
    if (state.activeCard) {
      const card = state.activeCard
      state.times[card] = (state.times[card] || 0) + 1
    }
  }),

  calculateTotalTime: action((state) => {
    const totalSeconds = Object.values(state.times).reduce((acc, seconds) => acc + seconds, 0)
    state.totalTime = formatTime(totalSeconds)
  }),

  updateExamination: thunk(async (actions, payload, { getState }) => {
    try {
      // Update local state immediately with full examination data (including tasks)
      const state = getState()
      const updatedProjects = state.projects.map((project) => {
        if (!project || !project.devices) {
          return project as any
        }
        const updatedDevices = project.devices.map((device) => {
          if (device && (device as any).examinations) {
            const updatedExaminations = (device as any).examinations.map((exam: any) =>
              exam && exam.id === payload.id ? payload : exam
            )
            return { ...device, examinations: updatedExaminations }
          }
          return device
        })
        return { ...project, devices: updatedDevices }
      })
      actions.setProjects(updatedProjects)

      console.log("Updated examination in local state with full data including tasks")

      // Sync examination data to backend including tasks
      if (
        payload.norm &&
        payload.norm.id &&
        payload.examinationConfig &&
        payload.examinationConfig.id
      ) {
        try {
          // Convert tasks for GraphQL (File objects to file IDs)
          const tasksForGraphQL =
            payload.tasks?.map((task) => ({
              id: task.id,
              name: task.name,
              info: task.info,
              taskSteps: task.taskSteps,
              estimatedTime: task.estimatedTime,
              workingTime: task.workingTime,
              status: task.status,
              examinator:
                typeof task.examinator === "object" ? task.examinator?.id : task.examinator,
              testDatas:
                task.testDatas?.map((testData) => {
                  // Debug log the image conversion
                  console.log("Converting testData.image:", testData.image)
                  const convertedImages =
                    testData.image?.map((file) => {
                      // Convert to ID string if it's a File object
                      return typeof file === "string" ? file : file.id
                    }) || []
                  console.log("Final converted images:", convertedImages)

                  const newTestData =
                    testData.deviceTestData?.map((item) => {
                      return {
                        ...item,
                        device: item.device.id,
                        images: item.images.map((image) => image.id),
                      }
                    }) || []

                  return {
                    ...testData,
                    image: convertedImages,
                    helper:
                      testData.helper?.map((helper) =>
                        typeof helper === "string" ? helper : helper.id
                      ) || [],
                    witnesses:
                      testData.witnesses
                        ?.map((witness) => {
                          // Handle null witnesses
                          if (!witness) {
                            return null
                          }
                          // If it's already a string ID, we need to return an empty ContactInput
                          // The backend will need to handle looking up the contact by ID
                          if (typeof witness === "string") {
                            return {
                              id: witness,
                              // Provide minimal required fields - backend should handle the rest
                            }
                          }
                          // If it's a Contact object, convert it to ContactInput
                          return {
                            id: witness.id,
                            contactID: witness.contactID || "",
                            title: witness.title || "",
                            jobTitle: witness.jobTitle || "",
                            firstName: witness.firstName || "",
                            lastName: witness.lastName || "",
                            displayName: witness.displayName || "",
                            email: witness.email || "",
                            companyName: witness.companyName || "",
                            gender: witness.gender || "",
                            phone1: witness.phone1 || "",
                            phone2: witness.phone2 || "",
                            phone3: witness.phone3 || "",
                            active: witness.active !== undefined ? witness.active : true,
                            businessAccount: witness.businessAccount || "",
                          }
                        })
                        .filter((w) => w !== null) || [],
                    deviceTestData: newTestData,
                    createdBy:
                      typeof testData.createdBy === "string"
                        ? testData.createdBy || "UNKNOWN"
                        : testData.createdBy?.id || "UNKNOWN",
                  }
                }) || [],
            })) || []

          // Debug log the entire tasks structure
          // console.log("Final tasksForGraphQL:", JSON.stringify(tasksForGraphQL, null, 2))

          // Convert history for GraphQL
          const historyForGraphQL =
            payload.history?.map((historyItem) => ({
              id: historyItem.id,
              description: historyItem.description,
              createdAt: historyItem.createdAt,
              createdBy: historyItem.createdBy?.id || null, // Handle nullable user for development
            })) || []

          // console.log("History for GraphQL:", JSON.stringify(historyForGraphQL, null, 2))

          const deviceSetupForGraphQL =
            payload.deviceSetup?.map((deviceSetup) => ({
              device: deviceSetup.device.id,
              note: deviceSetup.note,
              images: deviceSetup.images.map((image) => image.id) || [],
              linkedTo:
                deviceSetup.linkedTo?.map((linkedTo) => ({
                  device: linkedTo?.device?.id,
                  note: linkedTo?.note,
                  deviceType: linkedTo?.device?.type,
                })) || [],
            })) || []
          // Send examination with converted tasks and history

          console.log("testParameterBlock", payload.testParameterBlock)

          await apolloClient.mutate({
            mutation: UPSERT_EXAMINATION,
            variables: {
              input: {
                id: payload.id === "NEW" ? v4() : payload.id,
                name: payload.name,
                isAccredited: payload.isAccredited,
                norm: payload.norm.id,
                calculation: payload.calculation,
                examinationConfig: payload.examinationConfig.id,
                status: payload.status,
                specialArrangements: payload.specialArrangements,
                laboratory: payload.laboratory?.id || "",
                location: payload.location,
                numberOfRuns: payload.numberOfRuns,
                salesQuote: payload.salesQuote,
                startTimestamp: payload.startTimestamp,
                endTimestamp: payload.endTimestamp,
                deviations: payload.deviations,
                // New fields: persist tester and pass/fail criteria
                tester: payload.tester?.id || undefined,
                passFailCriteria: payload.passFailCriteria,
                tasks: tasksForGraphQL,
                history: historyForGraphQL,
                deviceSetup: deviceSetupForGraphQL,
                testParameterBlock: payload.testParameterBlock || [],
              },
            },
          })
          console.log("Synced examination fields and tasks to backend")

          // Then ensure tasks are persisted to the database
          if (payload.tasks && payload.tasks.length > 0) {
            console.log(
              `Ensuring ${payload.tasks.length} tasks are persisted for examination ${payload.id}`
            )
            // The server-side code will automatically persist tasks when the examination is loaded
            // We just need to make sure the examination exists in the database first
          }
        } catch (backendError) {
          console.warn("Backend sync failed, but local state updated:", backendError)
          // Don't throw error - local state is updated successfully
        }
      } else {
        console.log("Skipping backend sync - missing required fields, only updated local state")
      }
    } catch (error) {
      console.error("Error updating examination:", error)
      // Don't throw error if local state update succeeded
    }
  }),

  updateTaskWorkingTime: thunk(async (actions, payload, { getState }) => {
    try {
      await apolloClient.mutate({
        mutation: UPDATE_TASK_WORKING_TIME,
        variables: payload,
      })

      // After successful backend update, update the local state
      const state = getState()
      const updatedProjects = state.projects.map((project) => {
        if (!project || !project.devices) {
          return project as any
        }
        const updatedDevices = project.devices.map((device) => {
          if (device && (device as any).examinations) {
            const updatedExaminations = (device as any).examinations.map((exam: any) => {
              if (exam && exam.id === payload.examinationId) {
                const updatedTasks = (exam.tasks || []).map((task: any) => {
                  if (task && task.id === payload.taskId) {
                    return { ...task, workingTime: payload.workingTime }
                  }
                  return task
                })
                return { ...exam, tasks: updatedTasks }
              }
              return exam
            })
            return { ...device, examinations: updatedExaminations }
          }
          return device
        })
        return { ...project, devices: updatedDevices }
      })
      actions.setProjects(updatedProjects)
    } catch (error) {
      console.error("Error updating task working time:", error)
      enqueueSnackbar("Fehler beim Speichern der Arbeitszeit", { variant: "error" })
    }
  }),

  uploadFile: thunk(async (actions, payload) => {
    try {
      const { data } = await apolloClient.mutate({
        mutation: UPLOAD_FILE,
        variables: { input: payload },
      })
      return data.uploadFile
    } catch (error) {
      console.error("Error uploading file:", error)
    }
  }),

  updateReport: thunk(async (actions, payload) => {
    const { projectID, report } = payload

    // Helper function to transform DataNode to DataNodeInput
    const transformDataNodeToInput = (dataNode: DataNode): DataNodeInput => {
      const input: DataNodeInput = {
        dataType: dataNode.dataType,
        deletable: dataNode.deletable,
        editable: dataNode.editable,
        isAutoGenerated: dataNode.isAutoGenerated,
        sourceExaminationId: dataNode.sourceExaminationId,
        originalTextblockId: dataNode.originalTextblockId,
        originalTextBlockDataNodeNo: dataNode.originalTextBlockDataNodeNo,
        id: dataNode.id,
        text: dataNode.text,
        tableCaption: dataNode.tableCaption,
        tableHeader: dataNode.tableHeader,
        tableBody: dataNode.tableBody,
        tableNumber: dataNode.tableNumber,
        image: dataNode.image,
        imageCaption: dataNode.imageCaption,
        imageNumber: dataNode.imageNumber,
      }

      return input
    }

    // Helper function to transform Chapter to ChapterInput
    const transformChapterToInput = (chapter: Chapter): ChapterInput => {
      return {
        id: chapter.id,
        name: chapter.name,
        chapterNumber: chapter.chapterNumber,
        type: chapter.type,
        open: chapter.open,
        examinationId: chapter.examinationId,
        data: chapter.data.map(transformDataNodeToInput),
        subChapter: chapter.subChapter?.map(transformChapterToInput),
      }
    }

    try {
      const { data } = await apolloClient.mutate({
        mutation: UPSERT_REPORT,
        variables: {
          input: {
            id: report.id,
            name: report.name,
            title: report.title,
            documentNo: report.documentNo,
            version: report.version,
            status: report.status,
            info: report.info,
            reportURL: report.reportURL || "",
            examinations: report.examinations?.map((exam) => exam.id) || [],
            tester: report.tester?.map((tester) => tester.id) || [],
            chapter: report.chapter?.map(transformChapterToInput) || [],
            projectID: projectID,

            // Report settings fields
            classification: report.classification,
            issueStatus: report.issueStatus,
            assignmentNo: report.assignmentNo,
            releaseDate: report.releaseDate,
            formNumber: report.formNumber,
            formRevision: report.formRevision,
            reviewedById: report.reviewedBy?.id,
            reviewerTitle: report.reviewerTitle,
            showDraftWatermark: report.showDraftWatermark,
            accreditationStatus: report.accreditationStatus,
            testLocation: report.testLocation,
            testFacility: report.testFacility,
            qualificationTestPlan: report.qualificationTestPlan,
            revisions:
              report.revisions?.map((revision) => ({
                id: revision.id,
                revisionNumber: revision.revisionNumber,
                date: revision.date,
                affectedSections: revision.affectedSections,
                issuedById: revision.issuedBy?.id,
                reasonsForRevision: revision.reasonsForRevision,
              })) || [],
            versionNumber: report.versionNumber,
            parentVersionId: report.parentVersionId,
            versionComment: report.versionComment,
            createdBy: report.createdBy?.id,
          },
        },
      })

      // Check for validation warnings
      if (data?.upsertReport) {
        const response = data.upsertReport

        // Show success message
        enqueueSnackbar("Report erfolgreich gespeichert", { variant: "success" })

        // Show warnings if any examinations were filtered
        if (response.warnings && response.warnings.length > 0) {
          response.warnings.forEach((warning: string) => {
            enqueueSnackbar(warning, { variant: "warning", persist: true })
          })
        }

        // Show details about filtered examinations
        if (response.filteredExaminations && response.filteredExaminations.length > 0) {
          const details = response.filteredExaminations.join(", ")
          enqueueSnackbar(`Gefilterte Untersuchungen: ${details}`, {
            variant: "info",
            persist: true,
          })
        }
      }
    } catch (error) {
      console.error("Error updating report:", error)
      enqueueSnackbar("Error updating report", { variant: "error" })
    }
  }),

  updateProject: thunk(
    async (actions, payload: Project & { noSnackbar?: boolean }, { getState }) => {
      try {
        // Extract noSnackbar from payload and pass the rest to UPSERT_PROJECT
        const { noSnackbar, ...projectData } = payload

        const { data } = await apolloClient.mutate({
          mutation: UPSERT_PROJECT,
          variables: {
            input: {
              ...projectData,
              id: projectData.id === "NEW" ? v4() : projectData.id,
              lead: projectData.lead?.opportunityID || "",
              sales: projectData.sales?.id || "",
              management: projectData.management?.id || "",
              contacts: projectData.contacts,
              devices: projectData.devices ? projectData.devices.map((item) => item.id) : [],
              documents: projectData.documents ? projectData.documents.map((item) => item.id) : [],
            },
          },
        })

        if (data?.upsertProject) {
          const state = getState()
          const newProject: Project = { ...payload, id: data.upsertProject }
          if (payload.id === "NEW") {
            actions.setProjects([...state.projects, newProject])
          } else {
            actions.setProjects(
              state.projects
                .filter(Boolean)
                .map((project) => (project.id === payload.id ? newProject : project))
            )
          }
          console.log("payload.noSnackbar", !payload.noSnackbar)
          if (!payload.noSnackbar) {
            enqueueSnackbar("Projekt erfolgreich aktualisiert", {
              variant: "success",
            })
          }
        }
      } catch (error) {
        console.error("Error updating Project:", error)
      }
    }
  ),

  updateEutGroup: thunk(async (actions, payload, { getState }) => {
    try {
      const eutGroup = payload.deviceEutGroup
      const isOffer = payload.isOffer
      const { examinations, ...newPayload } = eutGroup
      const exs = examinations || []

      const { data } = await apolloClient.mutate({
        mutation: UPSERT_DEVICE_EUT_GROUP,
        variables: {
          input: {
            ...newPayload,
            id: eutGroup.id === "NEW" ? v4() : eutGroup.id,
            norms: eutGroup.norms ? eutGroup.norms.map((item) => item.id) : [],
            examinations: exs.map((item) => item.id),

            examinationAndModes: exs.map((item) => ({
              examination: item.id,
              operatingModes: item.operatingModes || [],
            })),

            devices: (eutGroup.devices || []).map((item) => item.id),
            images: (eutGroup.images || []).map((item) => item.id),
            documents: (eutGroup.documents || []).map((item) => item.id),
            deviceDataGroup: eutGroup.deviceDataGroup || [],
            notes: eutGroup.notes || [],
            movementData: eutGroup.movementData || [],
          },
          inputEut: (eutGroup.devices || []).map((item) => ({
            ...item,
            id: item.id === "NEW" ? v4() : item.id,
            images: item.images ? item.images.map((i) => i.id) : [],
            documents: item.documents ? item.documents.map((d) => d.id) : [],
            name: item.name !== "" ? item.name : eutGroup.name,
            description: item.description !== "" ? item.description : eutGroup.description,
            manufacturer: item.manufacturer || eutGroup.manufacturer || undefined,
            model: item.model || eutGroup.model || undefined,
            serialNumber: item.serialNumber || eutGroup.serialNumber || undefined,
            productNumber: item.productNumber || eutGroup.productNumber || undefined,
            revision: item.revision || eutGroup.revision || undefined,
            software: item.software || eutGroup.software || undefined,
            softwareVersion: item.softwareVersion || eutGroup.softwareVersion || undefined,
            function: item.function || eutGroup.function || undefined,
            type: DeviceType.EQUIPMENT_UNDER_TEST,
            note: item.note ? item.note : [],
            movementData: item.movementData ? item.movementData : [],
          })),
          isOffer: isOffer,
        },
      })

      if (data?.upsertDeviceEutGroup) {
        const state = getState()
        const newEutGroup: DeviceEutGroup = {
          ...eutGroup,
          id: data.upsertDeviceEutGroup,
        }
        if (eutGroup.id === "NEW") {
          actions.setEutGroups([...state.eutGroups, newEutGroup])
        } else {
          actions.setEutGroups(
            state.eutGroups.map((eG) => (eG.id === eutGroup.id ? newEutGroup : eG))
          )
        }
      }
    } catch (error) {
      console.error("Error updating Project:", error)
    }
  }),

  updateAux: thunk(async (actions, payload) => {
    try {
      await apolloClient.mutate({
        mutation: UPSERT_DEVICE_AUX,
        variables: {
          input: {
            ...payload,
            id: payload.id === "NEW" ? v4() : payload.id,
            images: payload.images.map((item) => item.id),
            documents: payload.documents ? payload.documents.map((item) => item.id) : [],
            manufacturer: payload.manufacturer || undefined,
            model: payload.model || undefined,
            serialNumber: payload.serialNumber || undefined,
            function: payload.function || undefined,
            movementData: payload.movementData || [],
            deviceDataGroup: payload.deviceDataGroup || [],
          },
        },
      })
    } catch (error) {
      console.error("Error updating Device Aux:", error)
    }
  }),

  updateSkill: thunk(async (actions, payload, { getState }) => {
    try {
      const { data } = await apolloClient.mutate({
        mutation: UPSERT_SKILL,
        variables: {
          input: {
            id: payload.id === "NEW" ? v4() : payload.id,
            name: payload.name,
            person: payload.person && payload.person.length > 0 ? payload.person : [],
            testEquipment:
              payload.testEquipment && payload.testEquipment.length > 0
                ? payload.testEquipment.map((t) => t.id)
                : [],
          },
        },
      })

      if (data?.upsertSkill) {
        const state = getState()
        const newSkill: Skill = { ...payload, id: data.upsertSkill }
        if (payload.id === "NEW") {
          actions.setSkills([...state.skills, newSkill])
        } else {
          actions.setSkills(
            state.skills.map((skill) => (skill.id === payload.id ? newSkill : skill))
          )
        }
      }
    } catch (error) {
      console.error("Error updating skill:", error)
    }
  }),

  updateCriteriaCatalog: thunk(async (actions, payload, { getState }) => {
    try {
      const { data } = await apolloClient.mutate({
        mutation: UPSERT_CRITERIA_CATALOG,
        variables: {
          input: {
            ...payload,
            id: payload.id === "NEW" ? v4() : payload.id,
          },
        },
      })

      if (data?.upsertCriteriaCatalog) {
        const state = getState()
        const newCriteriaCatalog: CriteriaCatalog = { ...payload, id: data.upsertCriteriaCatalog }
        if (payload.id === "NEW") {
          actions.setCriteriaCatalogs([...state.criteriaCatalogs, newCriteriaCatalog])
        } else {
          actions.setCriteriaCatalogs(
            state.criteriaCatalogs.map((criteriaCatalog) =>
              criteriaCatalog.id === payload.id ? newCriteriaCatalog : criteriaCatalog
            )
          )
        }
      }
    } catch (error) {
      console.error("Error updating skill:", error)
    }
  }),

  updateTextBlock: thunk(async (actions, payload, { getState }) => {
    const newTextBlock = {
      ...payload,
      id: payload.id === "NEW" ? v4() : payload.id,
    }

    //  set originalTextblockId and originalTextBlockDataNodeNo for each block

    newTextBlock.block = newTextBlock.block.map((dataNode, index) => ({
      ...dataNode,
      originalTextblockId: newTextBlock.id,
      originalTextBlockDataNodeNo: index,
    }))

    try {
      const { data } = await apolloClient.mutate({
        mutation: UPSERT_TEXT_BLOCK,
        variables: {
          input: {
            ...newTextBlock,
          },
        },
      })

      if (data?.upsertTextBlock) {
        const state = getState()
        if (payload.id === "NEW") {
          actions.setTextBlocks([...state.textBlocks, { ...payload, id: newTextBlock.id }])
        } else {
          actions.setTextBlocks(
            state.textBlocks.map((block) =>
              block.id === payload.id ? { ...payload, id: newTextBlock.id } : block
            )
          )
        }
      }
    } catch (error) {
      console.error("Error updating text block:", error)
    }
  }),

  updateNorm: thunk(async (actions, payload, { getState }) => {
    try {
      const newNorm = {
        id: payload.id === "NEW" ? v4() : payload.id,
        name: payload.name.trim(),
        branch: payload.branch.trim(),
        label: payload.label.trim(),
        revision: payload.revision.trim(),
        date: payload.date.trim(),
        documents: payload.documents.map((item) => item.id),
        examinationCalculations: payload.examinationCalculations
          ? payload.examinationCalculations.map((item) => {
              return {
                ...item,
                examinationConfig:
                  typeof item.examinationConfig === "string"
                    ? item.examinationConfig
                    : item.examinationConfig?.id || "",
                calculation: {
                  ...item.calculation,
                  id: item.calculation.id === "NEW" ? v4() : item.calculation.id,
                },
                tags: (item as any).tags
                  ? (item as any).tags.map((tag: { id?: string; name: string }) => ({
                      id: tag.id && tag.id !== "" ? tag.id : undefined,
                      name: tag.name,
                    }))
                  : [],
              }
            })
          : [],
        isOutdated: payload.isOutdated,
        isHarmonized: payload.isHarmonized,
        isDeleted: payload.isDeleted,
        deletedAt: payload.deletedAt,
      }

      const { data } = await apolloClient.mutate({
        mutation: UPSERT_NORM,
        variables: {
          input: newNorm,
        },
      })

      if (data?.upsertNorm) {
        const state = getState()
        if (payload.id === "NEW") {
          actions.setNorms([
            ...state.norms,
            {
              ...payload,
              id: newNorm.id,
              examinationCalculations: payload.examinationCalculations.map((item, index) => ({
                ...item,
                id: newNorm.examinationCalculations[index].id,
              })),
            },
          ])
        } else {
          actions.setNorms(
            state.norms.map((norm) =>
              norm.id === payload.id ? { ...payload, id: newNorm.id } : norm
            )
          )
        }
      }
    } catch (error) {
      console.error("Error updating norm:", error)
    }
  }),

  updateDeviceDataGroup: thunk(async (actions, payload, { getState }) => {
    try {
      const { data } = await apolloClient.mutate({
        mutation: UPSERT_DEVICE_DATA_GROUP,
        variables: {
          input: {
            id: payload.id === "NEW" ? v4() : payload.id,
            name: payload.name,
            deviceData: payload.deviceData,
            isDefault: payload.isDefault,
            isAux: payload.isAux,
          },
        },
      })

      if (data?.upsertDeviceDataGroup) {
        const state = getState()
        if (payload.id === "NEW") {
          actions.setDeviceDataGroups([...state.deviceDataGroups, data.upsertDeviceDataGroup])
        } else {
          actions.setDeviceDataGroups(
            state.deviceDataGroups.map((group) =>
              group.id === payload.id ? data.upsertDeviceDataGroup : group
            )
          )
        }
      }
    } catch (error) {
      console.error("Error updating device data group:", error)
    }
  }),

  updateTaskConfig: thunk(async (actions, payload, { getState }) => {
    try {
      const { data } = await apolloClient.mutate({
        mutation: UPSERT_TASK_CONFIG,
        variables: {
          input: {
            id: payload.id === "NEW" ? v4() : payload.id,
            name: payload.name,
            info: payload.info,
            taskSteps: payload.taskSteps,
          },
        },
      })

      if (data?.upsertTaskConfig) {
        const state = getState()
        if (payload.id === "NEW") {
          actions.setTaskConfigs([...state.taskConfigs, data.upsertTaskConfig])
        } else {
          actions.setTaskConfigs(
            state.taskConfigs.map((task) => (task.id === payload.id ? data.upsertTaskConfig : task))
          )
        }
      }
    } catch (error) {
      console.error("Error updating task:", error)
    }
  }),

  updateTestEquipment: thunk(async (actions, payload, { getState }) => {
    try {
      const generatedId = payload.id === "NEW" ? v4() : payload.id
      const trimmedStatus = payload.status?.trim()
      const trimmedLocation = payload.location?.trim()
      const normalizedLocation = trimmedLocation ? trimmedLocation.toUpperCase() : undefined
      const newTE: DeviceTestEquipment = {
        ...payload,
        id: generatedId,
        type: DeviceType.TEST_EQUIPMENT,
        status: trimmedStatus || undefined,
        location: normalizedLocation,
      }

      const input = {
        id: newTE.id,
        type: DeviceType.TEST_EQUIPMENT,
        name: newTE.name,
        description: newTE.description,
        barcode: newTE.barcode,
        manufacturer: newTE.manufacturer || undefined,
        model: newTE.model || undefined,
        serialNumber: newTE.serialNumber || undefined,
        function: newTE.function || undefined,
        images: (newTE.images || []).map((image) => image.id),
        documents: (newTE.documents || []).map((doc) => doc.id),
        inventoryNo: newTE.inventoryNo,
        calibrationDate: newTE.calibrationDate || undefined,
        calibrationDue: newTE.calibrationDue,
        uncertainty: newTE.uncertainty,
        teType: newTE.teType,
        status: newTE.status || undefined,
        location: newTE.location || undefined,
      }

      const { data } = await apolloClient.mutate({
        mutation: UPSERT_DEVICE_TEST_EQUIPMENT,
        variables: {
          input,
        },
      })

      if (data?.upsertDeviceTestEquipment) {
        const state = getState()
        const updatedTestEquipment = newTE

        if (payload.id === "NEW") {
          actions.setTestEquipment([...state.testEquipment, updatedTestEquipment])
        } else {
          actions.setTestEquipment(
            state.testEquipment.map((item) =>
              item.id === payload.id ? { ...item, ...updatedTestEquipment } : item
            )
          )
        }
      }
    } catch (error) {
      console.error("Error updating test equipment:", error)
    }
  }),

  updateAuxEquipment: thunk(async (actions, payload, { getState }) => {
    try {
      const newAux = {
        ...payload,
        id: payload.id === "NEW" ? v4() : payload.id,
        type: DeviceType.AUX,
      }
      const { data } = await apolloClient.mutate({
        mutation: UPSERT_DEVICE_AUX,
        variables: {
          input: {
            id: newAux.id,
            type: DeviceType.AUX,
            name: newAux.name,
            barcode: newAux.barcode,
            description: newAux.description,
            manufacturer: newAux.manufacturer || undefined,
            model: newAux.model || undefined,
            serialNumber: newAux.serialNumber || undefined,
            function: newAux.function || undefined,
            images: (newAux.images || []).map((img) => img.id),
            movementData: newAux.movementData || [],
            deviceDataGroup: (newAux.deviceDataGroup || []).map((group) => ({
              id: group.id,
              name: group.name,
              isAux: group.isAux,
              isDefault: group.isDefault,
              deviceData: (group.deviceData || []).map((field) => ({
                id: field.id,
                name: field.name,
                dataType: field.dataType,
                unit: field.unit || [],
                isArray: field.isArray,
                isCalculation: field.isCalculation,
                isOperatingMode: field.isOperatingMode,
                data: field.data || [],
              })),
            })),
          },
        },
      })

      if (data?.upsertDeviceAux) {
        const state = getState()
        if (payload.id === "NEW") {
          actions.setAuxEquipment([...state.auxEquipment, newAux])
        } else {
          actions.setAuxEquipment(
            state.auxEquipment.map((item) => (item.id === payload.id ? newAux : item))
          )
        }
      }
    } catch (error) {
      console.error("Error updating AUX equipment:", error)
    }
  }),

  updateExaminationConfig: thunk(async (actions, payload, { getState }) => {
    try {
      const newExConf: ExaminationConfigInput = {
        id: payload.id === "NEW" ? v4() : payload.id,
        skill: payload.skill ? payload.skill.id : "",
        laboratory: payload.laboratory ? payload.laboratory.id : "",
        location: payload.location,
        eutDataGroups: payload.eutDataGroups?.map((item) => item.id) ?? [],
        tasks: payload.tasks?.map((item) => item.id) ?? [],
        name: payload.name,
        article: payload.article,
        defaultCalculation: {
          id: payload.defaultCalculation.id === "NEW" ? v4() : payload.defaultCalculation.id,
          calculationType: payload.defaultCalculation.calculationType,
          offerText: payload.defaultCalculation.offerText,
          humanPrice: payload.defaultCalculation.humanPrice,
          machinePrice: payload.defaultCalculation.machinePrice,
          connectionPrice: payload.defaultCalculation.connectionPrice,
        },
      }

      const { data } = await apolloClient.mutate({
        mutation: UPSERT_EXAMINATION_CONFIG,
        variables: {
          input: {
            ...newExConf,
          },
        },
      })

      if (data?.upsertExaminationConfig) {
        const state = getState()
        if (payload.id === "NEW") {
          actions.setExaminationConfigs([
            ...state.examinationConfigs,
            {
              ...payload,
              id: newExConf.id,
              defaultCalculation: {
                ...newExConf.defaultCalculation,
                id: newExConf.defaultCalculation.id,
              },
            },
          ])
        } else {
          actions.setExaminationConfigs(
            state.examinationConfigs.map((config) =>
              config.id === payload.id
                ? {
                    ...payload,
                    id: newExConf.id,
                    defaultCalculation: {
                      ...newExConf.defaultCalculation,
                      id: newExConf.defaultCalculation.id,
                    },
                  }
                : config
            )
          )
        }
      }
    } catch (error) {
      console.error("Error updating examination config:", error)
    }
  }),

  updateLaboratory: thunk(async (actions, payload, { getState }) => {
    try {
      const newLaboratory = {
        ...payload,
        id: payload.id === "NEW" ? v4() : payload.id,
        manager: payload.manager ? payload.manager : "",
      }

      const { data } = await apolloClient.mutate({
        mutation: UPSERT_LABORATORY,
        variables: {
          input: {
            ...newLaboratory,
          },
        },
      })

      if (data?.upsertLaboratory) {
        const state = getState()
        if (payload.id === "NEW") {
          actions.setLaboratories([...state.laboratories, { ...payload, id: newLaboratory.id }])
        } else {
          actions.setLaboratories(
            state.laboratories.map((lab) =>
              lab.id === payload.id ? { ...payload, id: newLaboratory.id } : lab
            )
          )
        }
      }
    } catch (error) {
      console.error("Error updating laboratory:", error)
    }
  }),

  deleteSkill: thunk(async (actions, ids, { getState }) => {
    try {
      await apolloClient.mutate({
        mutation: DELETE_SKILL,
        variables: { ids },
      })
      const state = getState()
      const newSkills = state.skills.filter((item) => !ids.includes(item.id))
      actions.setSkills(newSkills)
    } catch (error) {
      console.error("Error deleting skills:", error)
    }
  }),

  deleteCriteriaCatalog: thunk(async (actions, ids, { getState }) => {
    try {
      await apolloClient.mutate({
        mutation: DELETE_CRITERIA_CATALOG,
        variables: { ids },
      })
      const state = getState()
      const newCriteriaCatalogs = state.criteriaCatalogs.filter((item) => !ids.includes(item.id))
      actions.setCriteriaCatalogs(newCriteriaCatalogs)
    } catch (error) {
      console.error("Error deleting criteria catalogs:", error)
    }
  }),

  deleteTextBlock: thunk(async (actions, ids, { getState }) => {
    try {
      await apolloClient.mutate({
        mutation: DELETE_TEXT_BLOCK,
        variables: { ids },
      })
      const state = getState()
      const newBlocks = state.textBlocks.filter((item) => !ids.includes(item.id))
      actions.setTextBlocks(newBlocks)
    } catch (error) {
      console.error("Error deleting text blocks:", error)
    }
  }),

  deleteNorm: thunk(async (actions, ids, { getState }) => {
    try {
      // Use soft-delete mutation to preserve connections
      await apolloClient.mutate({
        mutation: SET_NORMS_DELETED,
        variables: { ids, deleted: true, deletedAt: new Date().toISOString() },
      })
      const state = getState()
      const newNorms = state.norms.map((n) =>
        ids.includes(n.id) ? { ...n, isDeleted: true, deletedAt: new Date().toISOString() } : n
      )
      actions.setNorms(newNorms)
    } catch (error) {
      console.error("Error deleting norms:", error)
    }
  }),

  setNormsDeleted: thunk(async (actions, payload, { getState }) => {
    try {
      await apolloClient.mutate({
        mutation: SET_NORMS_DELETED,
        variables: payload,
      })
      const state = getState()
      const now = new Date().toISOString()
      const updated = state.norms.map((n) =>
        payload.ids.includes(n.id)
          ? {
              ...n,
              isDeleted: payload.deleted,
              deletedAt: payload.deleted ? payload.deletedAt || now : undefined,
            }
          : n
      )
      actions.setNorms(updated)
    } catch (error) {
      console.error("Error setting norms deleted:", error)
    }
  }),

  deleteDeviceDataGroup: thunk(async (actions, ids, { getState }) => {
    try {
      await apolloClient.mutate({
        mutation: DELETE_DEVICE_DATA_GROUP,
        variables: { ids },
      })
      const state = getState()
      const newDeviceDataGroups = state.deviceDataGroups.filter((item) => !ids.includes(item.id))
      actions.setDeviceDataGroups(newDeviceDataGroups)
    } catch (error) {
      console.error("Error deleting device data groups:", error)
    }
  }),

  deleteTaskConfig: thunk(async (actions, ids, { getState }) => {
    try {
      await apolloClient.mutate({
        mutation: DELETE_TASK_CONFIG,
        variables: { ids },
      })
      const state = getState()
      const newTaskConfigs = state.taskConfigs.filter((item) => !ids.includes(item.id))
      actions.setTaskConfigs(newTaskConfigs)
    } catch (error) {
      console.error("Error deleting tasks:", error)
    }
  }),

  deleteTestEquipment: thunk(async (actions, ids, { getState }) => {
    try {
      await apolloClient.mutate({
        mutation: DELETE_DEVICE,
        variables: { ids },
      })
      const state = getState()
      const newTestEquipment = state.testEquipment.filter((item) => !ids.includes(item.id))
      actions.setTestEquipment(newTestEquipment)
    } catch (error) {
      console.error("Error deleting test equipment:", error)
    }
  }),

  deleteAuxEquipment: thunk(async (actions, ids, { getState }) => {
    try {
      await apolloClient.mutate({
        mutation: DELETE_DEVICE,
        variables: { ids },
      })
      const state = getState()
      const newAuxEquipment = state.auxEquipment.filter((item) => !ids.includes(item.id))
      actions.setAuxEquipment(newAuxEquipment)
    } catch (error) {
      console.error("Error deleting AUX equipment:", error)
    }
  }),

  deleteExaminationConfig: thunk(async (actions, ids, { getState }) => {
    try {
      await apolloClient.mutate({
        mutation: SET_EXAM_CONFIGS_OBSOLETE,
        variables: { ids, obsolete: true, obsoleteAt: new Date().toISOString() },
      })
      const state = getState()
      const updated = state.examinationConfigs.map((c) =>
        ids.includes(c.id) ? { ...c, isObsolete: true, obsoleteAt: new Date().toISOString() } : c
      )
      actions.setExaminationConfigs(updated)
    } catch (error) {
      console.error("Error deleting examination configs:", error)
    }
  }),

  setExaminationConfigsObsolete: thunk(async (actions, payload, { getState }) => {
    try {
      await apolloClient.mutate({
        mutation: SET_EXAM_CONFIGS_OBSOLETE,
        variables: payload,
      })
      const state = getState()
      const now = new Date().toISOString()
      const updated = state.examinationConfigs.map((c) =>
        payload.ids.includes(c.id)
          ? {
              ...c,
              isObsolete: payload.obsolete,
              obsoleteAt: payload.obsolete ? payload.obsoleteAt || now : undefined,
            }
          : c
      )
      actions.setExaminationConfigs(updated)
    } catch (error) {
      console.error("Error setting examination configs obsolete:", error)
    }
  }),

  deleteLaboratory: thunk(async (actions, ids, { getState }) => {
    try {
      await apolloClient.mutate({
        mutation: DELETE_LABORATORY,
        variables: { ids },
      })
      const state = getState()
      const newLaboratories = state.laboratories.filter((item) => !ids.includes(item.id))
      actions.setLaboratories(newLaboratories)
    } catch (error) {
      console.error("Error deleting laboratories:", error)
    }
  }),

  deleteFile: thunk(async (actions, ids) => {
    try {
      await apolloClient.mutate({
        mutation: DELETE_FILE,
        variables: { ids },
      })
    } catch (error) {
      console.error("Error deleting laboratories:", error)
    }
  }),

  setTextBlocks: action((state, payload) => {
    state.textBlocks = payload
  }),

  setNorms: action((state, payload) => {
    state.norms = payload
  }),

  setDeviceDataGroups: action((state, payload) => {
    state.deviceDataGroups = payload
  }),

  setTaskConfigs: action((state, payload) => {
    state.taskConfigs = payload
  }),

  setExaminationConfigs: action((state, payload) => {
    state.examinationConfigs = payload
  }),

  setLaboratories: action((state, payload) => {
    state.laboratories = payload
  }),

  setSkills: action((state, payload) => {
    state.skills = payload
  }),

  setTestEquipment: action((state, payload) => {
    state.testEquipment = payload
  }),

  setAuxEquipment: action((state, payload) => {
    state.auxEquipment = payload
  }),

  setContacts: action((state, payload) => {
    state.contacts = payload
  }),

  setCustomers: action((state, payload) => {
    state.customers = payload
  }),

  setSalesQuotes: action((state, payload) => {
    state.salesQuotes = payload
  }),

  setArticles: action((state, payload) => {
    state.articles = payload
  }),

  setUsers: action((state, payload) => {
    state.users = payload
  }),

  setAuthenticatedUser: action((state, payload) => {
    state.authenticatedUser = payload
  }),

  setSalesQuoteDetails: action((state, payload) => {
    state.salesQuoteDetails = payload
  }),

  setProjects: action((state, payload) => {
    state.projects = payload
  }),

  setEutGroups: action((state, payload) => {
    state.eutGroups = payload
  }),

  setEmployees: action((state, payload) => {
    state.employees = payload
  }),

  setReports: action((state, payload) => {
    state.reports = payload
  }),

  setCriteriaCatalogs: action((state, payload) => {
    state.criteriaCatalogs = payload
  }),

  setNormTagFilter: action((state, payload) => {
    if (payload.scope === "admin") {
      state.normFilters.admin = {
        ...state.normFilters.admin,
        ...payload.value,
      }
      return
    }
    const { projectId, value } = payload
    if (!projectId) {
      return
    }
    const current = state.normFilters.project[projectId] || { ...DEFAULT_TAG_FILTER }
    state.normFilters.project[projectId] = {
      ...current,
      ...value,
    }
  }),

  resetNormTagFilter: action((state, payload) => {
    if (payload.scope === "admin") {
      state.normFilters.admin = { ...DEFAULT_TAG_FILTER }
      return
    }
    const { projectId } = payload
    if (!projectId) {
      return
    }
    delete state.normFilters.project[projectId]
  }),

  createSalesQuote: thunk(async (actions, payload) => {
    // Signal start of Haufe operation
    haufeLoadingManager.startHaufeOperation(HAUFE_OPERATIONS.CREATE_SALES_QUOTE)

    try {
      console.log("Creating sales quote from opportunity...", payload)
      const response = await graphqlRequest(CREATE_SALES_QUOTE, {
        input: payload,
      })

      if (
        response.data &&
        response.data.data &&
        typeof response.data.data.createSalesQuoteFromOpportunity === "boolean"
      ) {
        const success = response.data.data.createSalesQuoteFromOpportunity
        if (success) {
          console.log("Sales quote creation reported as successful by the API.")
          actions.fetchSalesQuotes({ fresh: true })
        } else {
          console.warn("Sales quote creation reported as FAILED by the API (returned false).")
          throw new Error(
            response.data?.errors?.[0]?.message ||
              "Sales quote creation failed as indicated by API."
          )
        }
        return success
      } else {
        console.error(
          "Could not interpret sales quote creation response or error occurred:",
          response.data?.errors
        )
        throw new Error(
          response.data?.errors?.[0]?.message ||
            "Error creating sales quote or invalid response format."
        )
      }
    } catch (error) {
      console.error("Error creating sales quote:", error)
      throw error
    } finally {
      // Signal end of Haufe operation
      haufeLoadingManager.endHaufeOperation()
    }
  }),

  updateSalesQuote: thunk(async (actions, payload) => {
    // Signal start of Haufe operation
    haufeLoadingManager.startHaufeOperation(HAUFE_OPERATIONS.UPDATE_SALES_QUOTE)

    try {
      console.log("Updating sales quote...", payload)
      const response = await graphqlRequest(UPDATE_SALES_QUOTE, {
        input: payload,
      })

      if (response.data?.data?.updateSalesQuote) {
        const updatedSalesQuote = response.data.data.updateSalesQuote
        console.log("Sales quote updated:", updatedSalesQuote)
        return updatedSalesQuote
      } else {
        console.error(
          "Could not find updated sales quote data in GraphQL response or error occurred:",
          response.data?.errors
        )
        throw new Error(response.data?.errors?.[0]?.message || "Error updating sales quote")
      }
    } catch (error) {
      console.error("Error updating sales quote:", error)
      throw error
    } finally {
      // Signal end of Haufe operation
      haufeLoadingManager.endHaufeOperation()
    }
  }),

  getSalesQuoteDetails: thunk(async (actions, payload) => {
    try {
      console.log("Fetching sales quote details for quote ID:", payload.quoteID)
      const response = await graphqlRequest(GET_SALES_QUOTE_DETAILS, {
        quoteID: payload.quoteID,
      })

      if (response.data?.data?.getSalesQuoteDetailsByQuoteID) {
        const details = response.data.data.getSalesQuoteDetailsByQuoteID
        console.log("Sales quote details fetched:", details.length)
        actions.setSalesQuoteDetails(details)
        return details
      } else {
        console.error(
          "Could not find sales quote details in GraphQL response or error occurred:",
          response.data?.errors
        )
        actions.setSalesQuoteDetails([])
        return []
      }
    } catch (error) {
      console.error("Error fetching sales quote details:", error)
      actions.setSalesQuoteDetails([])
      return []
    }
  }),

  getSalesQuoteByQuoteNbrWithDetails: thunk(async (actions, payload, { getState }) => {
    try {
      console.log("Fetching sales quote with details for quote Nbr:", payload.quoteNbr)
      const response = await graphqlRequest(GET_SALES_QUOTE_BY_QUOTE_NBR_WITH_DETAILS, {
        quoteNbr: payload.quoteNbr,
      })

      if (response.data?.data?.getSalesQuoteByQuoteNbrWithDetails) {
        const result = response.data.data.getSalesQuoteByQuoteNbrWithDetails
        console.log("Sales quote with details fetched:", result)

        // Update both the salesQuotes and salesQuoteDetails in the store
        const state = getState()
        const updatedQuotes = state.salesQuotes.map((quote) =>
          quote.id === result.salesQuote.id ? result.salesQuote : quote
        )
        // If the quote doesn't exist in the store, add it
        if (!state.salesQuotes.find((quote) => quote.id === result.salesQuote.id)) {
          updatedQuotes.push(result.salesQuote)
        }
        actions.setSalesQuotes(updatedQuotes)
        actions.setSalesQuoteDetails(result.details)

        return result
      } else {
        console.error(
          "Could not find sales quote with details in GraphQL response or error occurred:",
          response.data?.errors
        )
        return null
      }
    } catch (error) {
      console.error("Error fetching sales quote with details:", error)
      return null
    }
  }),

  createContact: thunk(async (actions, payload, { getState }) => {
    try {
      console.log("Creating contact...", payload)
      const response = await graphqlRequest(CREATE_CONTACT, { input: payload })

      if (response.data?.data?.createContact) {
        const newContact = response.data.data.createContact
        console.log("Contact created:", newContact)

        const state = getState()
        // Avoid duplicates if it somehow already exists by ID
        if (!state.contacts.find((c) => c.id === newContact.id)) {
          actions.setContacts([...state.contacts, newContact])
        }
        return newContact
      } else {
        console.error(
          "Could not find contact data in GraphQL response or error occurred:",
          response.data?.errors
        )
        throw new Error(response.data?.errors?.[0]?.message || "Error creating contact")
      }
    } catch (error) {
      console.error("Error creating contact:", error)
      throw error // Re-throw to be caught by the calling component
    }
  }),

  createCustomer: thunk(async (actions, payload, { getState }) => {
    try {
      console.log("Creating customer...", payload)
      const response = await graphqlRequest(CREATE_CUSTOMER, { input: payload })

      if (response.data?.data?.createCustomer) {
        const newCustomer = response.data.data.createCustomer
        console.log("Customer created:", newCustomer)

        const state = getState()
        if (!state.customers.find((c) => c.id === newCustomer.id)) {
          actions.setCustomers([...state.customers, newCustomer])
        }
        return newCustomer
      } else {
        console.error(
          "Could not find customer data in GraphQL response or error occurred:",
          response.data?.errors
        )
        throw new Error(response.data?.errors?.[0]?.message || "Error creating customer")
      }
    } catch (error) {
      console.error("Error creating customer:", error)
      throw error
    }
  }),

  updateCustomer: thunk(async (actions, payload, { getState }) => {
    try {
      console.log("Updating customer...", payload)
      const response = await graphqlRequest(UPDATE_CUSTOMER, { input: payload })

      if (response.data?.data?.updateCustomer) {
        const updatedCustomer = response.data.data.updateCustomer
        console.log("Customer updated:", updatedCustomer)

        const state = getState()
        actions.setCustomers(
          state.customers.map((c) => (c.id === updatedCustomer.id ? updatedCustomer : c))
        )

        // Fetch the updated list of customers
        try {
          console.log("Refetching customer by ID after update...", updatedCustomer.customerID)
          await actions.getCustomerById({
            customerID: updatedCustomer.customerID,
            fresh: true,
          })
        } catch (fetchError) {
          console.error("Failed to refetch customer by ID after update:", fetchError)
        }

        return updatedCustomer
      } else {
        console.error(
          "Could not find updated customer data in GraphQL response or error occurred:",
          response.data?.errors
        )
        throw new Error(response.data?.errors?.[0]?.message || "Error updating customer")
      }
    } catch (error) {
      console.error("Error updating customer:", error)
      throw error
    }
  }),

  getCustomerById: thunk(async (actions, payload, { getState }) => {
    try {
      console.log(`Fetching customer by ID: ${payload.customerID}, fresh: ${!!payload.fresh}`)
      const response = await graphqlRequest(GET_CUSTOMER_BY_ID, {
        customerID: payload.customerID,
        fresh: !!payload.fresh,
      })

      if (response.data?.data?.getCustomerById) {
        const fetchedCustomer = response.data.data.getCustomerById
        console.log("Customer fetched by ID:", fetchedCustomer)

        const state = getState()
        const existingCustomerIndex = state.customers.findIndex((c) => c.id === fetchedCustomer.id)
        if (existingCustomerIndex !== -1) {
          const updatedCustomers = [...state.customers]
          updatedCustomers[existingCustomerIndex] = fetchedCustomer
          actions.setCustomers(updatedCustomers)
        } else {
          // If customer not in list, add it (though typically it should be after an update)
          actions.setCustomers([...state.customers, fetchedCustomer])
        }
        return fetchedCustomer
      } else {
        console.error(
          "Could not find customer data in getCustomerById GraphQL response or error occurred:",
          response.data?.errors
        )
        throw new Error(response.data?.errors?.[0]?.message || "Error fetching customer by ID")
      }
    } catch (error) {
      console.error("Error fetching customer by ID:", error)
      throw error
    }
  }),

  updateContact: thunk(async (actions, payload, { getState }) => {
    // actions._setContactsLoading(true); // Assuming you'll add loading/error state management
    try {
      console.log("Updating contact...", payload)
      const response = await graphqlRequest(UPDATE_CONTACT, { input: payload })

      if (response.data?.data?.updateContact) {
        const updatedContact = response.data.data.updateContact as Contact // Cast to local Contact type
        console.log("Contact updated:", updatedContact)

        const state = getState()
        actions.setContacts(
          state.contacts.map((c) => (c.id === updatedContact.id ? updatedContact : c))
        )

        // Optional: If you have a selectedContact state for a detail view, update it too
        // if (state.selectedContact && state.selectedContact.id === updatedContact.id) {
        //   actions._setSelectedContactDetail(updatedContact); // Assuming an action like this
        // }

        // Refetch the specific contact to ensure freshest data, especially if not all fields are returned by mutation
        if (updatedContact.contactID) {
          // Ensure contactID exists before trying to refetch
          try {
            console.log("Refetching contact by ID after update...", updatedContact.contactID)
            // Assuming getContactById thunk updates the selectedContact and potentially the list again
            await actions.getContactById({
              contactID: updatedContact.contactID,
              fresh: true,
            })
          } catch (fetchError) {
            console.error("Failed to refetch contact by ID after update:", fetchError)
          }
        } else {
          console.warn(
            "Cannot refetch contact by ID: contactID is missing from updated contact data."
          )
        }

        // actions._setContactsLoading(false);
        return updatedContact as any // Cast to any if GqlContact and Contact are slightly different and thunk expects GqlContact
      } else {
        console.error(
          "Could not find updated contact data in GraphQL response or error occurred:",
          response.data?.errors
        )
        // actions._setContactsError(response.data?.errors?.[0]?.message || "Error updating contact");
        // actions._setContactsLoading(false);
        throw new Error(
          response.data?.errors?.[0]?.message || "Error updating contact" // Corrected error message
        )
      }
    } catch (error) {
      console.error("Error updating contact:", error)
      throw error
    }
  }),

  getContactById: thunk(async (actions, payload, { getState }) => {
    try {
      console.log(`Fetching contact by ID: ${payload.contactID}, fresh: ${!!payload.fresh}`)
      const response = await graphqlRequest(GET_CONTACT_BY_ID, {
        contactID: payload.contactID,
        fresh: !!payload.fresh,
      })

      if (response.data?.data?.getContactById) {
        const fetchedContact = response.data.data.getContactById as Contact
        console.log("Contact fetched by ID:", fetchedContact)

        const state = getState()
        const existingContactIndex = state.contacts.findIndex((c) => c.id === fetchedContact.id)
        if (existingContactIndex !== -1) {
          const updatedContacts = [...state.contacts]
          updatedContacts[existingContactIndex] = fetchedContact
          actions.setContacts(updatedContacts)
        } else {
          actions.setContacts([...state.contacts, fetchedContact])
        }
        return fetchedContact as any
      } else {
        console.error(
          "Could not find contact data in getContactById GraphQL response or error occurred:",
          response.data?.errors
        )
        throw new Error(response.data?.errors?.[0]?.message || "Error fetching contact by ID")
      }
    } catch (error) {
      console.error("Error fetching contact by ID:", error)
      throw error
    }
  }),

  importNormenFromExcel: thunk(async (_actions, input, _helpers) => {
    try {
      const response = await apolloClient.mutate({
        mutation: IMPORT_NORMEN,
        variables: { input },
      })

      if (response.data?.importNormenFromExcel === true) {
        return { success: true, message: "Import erfolgreich" }
      }

      const errorMessage = response.errors?.[0]?.message || "Unbekannter Fehler beim Import"
      return { success: false, message: errorMessage }
    } catch (error) {
      console.warn("Apollo ImportNormen Fehler:", error)
      return {
        success: false,
        message: (error as Error)?.message || "Import fehlgeschlagen durch Netzwerkfehler",
      }
    }
  }),
})
